/**
 * Presentation selector. An explicit, persisted device mode chooses the shell;
 * the desktop docking code is code-split via `React.lazy` so it is never
 * downloaded in tablet/mobile mode (WS-016, ADR-UI-001 decision 5).
 */
import { lazy, Suspense } from "react";
import type { ReactNode } from "react";
import { observer } from "mobx-react-lite";
import { StackedWorkspace } from "./stacked-workspace";
import type { PanelContentRegistry } from "./content";
import type { WorkspaceStore } from "./workspace-store";
import "./workspace.css";

const LazyDesktopWorkspace = lazy(async () => import("./desktop-workspace"));

export interface WorkspaceViewProps {
  readonly store: WorkspaceStore;
  readonly contentRegistry?: PanelContentRegistry | undefined;
  readonly onRemovePanel?: ((id: string) => void) | undefined;
}

export const WorkspaceView = observer(function WorkspaceView({
  contentRegistry,
  onRemovePanel,
  store,
}: WorkspaceViewProps): ReactNode {
  if (store.deviceMode === "desktop") {
    return (
      <Suspense fallback={<p role="status">{store.text("workspaceLabel")}</p>}>
        <LazyDesktopWorkspace
          contentRegistry={contentRegistry}
          onRemovePanel={onRemovePanel}
          store={store}
        />
      </Suspense>
    );
  }
  return (
    <StackedWorkspace contentRegistry={contentRegistry} mode={store.deviceMode} store={store} />
  );
});
